import { PHASE } from "../../../../../common/constants.ts";
import { classifyPlayOption } from "../../util/playMenuGroups.ts";

// G2-1: a one-line heads-up rendered under each 远组 (skip-ahead) play option in
// the dashboard PlaySheet. The engine's schedule model has no cheap
// days-until-a-future-phase figure, and opening a fresh worker computation just
// to count them is out of scope (法则: no new heavy worker compute for this).
// So the preview degrades to what IS cheap and true on the UI side:
//   - a short descriptor of the span the sim will fast-forward (derived from the
//     option id — "推不出天数就显示动作将跨越的阶段名"), and
//   - a decision-window crossing warning ONLY when it can be judged cheaply from
//     the current phase + the option set already shipped to the UI (the trade
//     deadline still being ahead, the re-signing window still being open).
// Anything not cheaply derivable is omitted — the option label alone shows.

export type AdvancePreviewOption = {
	id: string;
	url?: string;
};

export type AdvancePreview = {
	// Base span descriptor (always present when a preview renders).
	spanKey: string;
	spanDefault: string;
	// Optional decision-window crossing warning appended below the span.
	crossKey?: string;
	crossDefault?: string;
};

// Each 远组 sim option → a short span descriptor. These are framed as "you are
// fast-forwarding through the games in between", which is the awareness the
// audit says is missing (D2: "快进会模拟几天?会跳过哪些决策窗口?"), rather than a
// bare restatement of the option's own destination label.
const SPAN_DESCRIPTORS: Record<string, { key: string; default: string }> = {
	untilAllStarGame: {
		key: "leagueDashboard.advancePreview.spanAllStar",
		default: "Auto-sims every game up to the All-Star break.",
	},
	untilTradeDeadline: {
		key: "leagueDashboard.advancePreview.spanTradeDeadline",
		default: "Auto-sims every game up to the trade deadline.",
	},
	untilPlayoffs: {
		key: "leagueDashboard.advancePreview.spanPlayoffs",
		default: "Auto-sims the rest of the regular season.",
	},
	throughPlayoffs: {
		key: "leagueDashboard.advancePreview.spanThroughPlayoffs",
		default: "Auto-sims the rest of the playoffs.",
	},
	untilDraft: {
		key: "leagueDashboard.advancePreview.spanDraft",
		default: "Fast-forwards through the offseason to draft night.",
	},
	untilEnd: {
		key: "leagueDashboard.advancePreview.spanDraftEnd",
		default: "Runs the draft through to the final pick.",
	},
	untilResignPlayers: {
		key: "leagueDashboard.advancePreview.spanResign",
		default: "Fast-forwards to the re-signing window.",
	},
	untilFreeAgency: {
		key: "leagueDashboard.advancePreview.spanFreeAgency",
		default: "Fast-forwards to the start of free agency.",
	},
	untilPreseason: {
		key: "leagueDashboard.advancePreview.spanPreseason",
		default: "Fast-forwards through the offseason to the preseason.",
	},
	untilRegularSeason: {
		key: "leagueDashboard.advancePreview.spanRegularSeason",
		default: "Fast-forwards to opening night of the new season.",
	},
};

const CROSS_TRADE_DEADLINE = {
	key: "leagueDashboard.advancePreview.crossTradeDeadline",
	default: "You will pass the trade deadline — trades lock after it.",
};

const CROSS_RESIGN = {
	key: "leagueDashboard.advancePreview.crossResign",
	default: "This ends your window to re-sign your own players.",
};

// Build the preview for one option, or undefined when nothing cheap can be said
// (the option is a 近组 step, a navigation-only 远组 entry like "view draft", or a
// sim with no mapped span descriptor). `options` is the same play-menu list the
// UI already has — used only for the cheap "is the trade deadline still ahead?"
// check (its option is present exactly while the deadline has not yet passed).
export const getAdvancePreview = (
	option: AdvancePreviewOption,
	phase: number | undefined,
	options: AdvancePreviewOption[],
): AdvancePreview | undefined => {
	// Navigation-only 远组 entries (viewDraft / viewAllStar / seasonSummary…) carry
	// a url and do not simulate — no fast-forward, no preview.
	if (option.url) {
		return undefined;
	}
	if (classifyPlayOption(option.id) !== "far") {
		return undefined;
	}

	const span = SPAN_DESCRIPTORS[option.id];
	if (!span) {
		return undefined;
	}

	let cross: { key: string; default: string } | undefined;
	if (
		option.id === "untilPlayoffs" &&
		// The trade deadline is still ahead exactly while its own option is offered
		// (updatePlayMenu drops it once the deadline passes). Cheap + correct, and
		// it also covers the "deadline disabled" league (option never appears).
		options.some((candidate) => candidate.id === "untilTradeDeadline")
	) {
		cross = CROSS_TRADE_DEADLINE;
	} else if (
		option.id === "untilFreeAgency" &&
		phase === PHASE.RESIGN_PLAYERS
	) {
		cross = CROSS_RESIGN;
	}

	return {
		spanKey: span.key,
		spanDefault: span.default,
		...(cross ? { crossKey: cross.key, crossDefault: cross.default } : {}),
	};
};
